import useSWR from "swr";
import { SelfSchedulerRowResponse } from "../../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import { DEFAULT_INSTALL_SCHEDULING_DAYS_OUT } from "../constants/capInstallScheduling";
import { resolveSelfSchedulerRow } from "../utils/installBookingRecovery";
import { useAuthToken } from "hooks/useAuthToken";
import { getInstallSelfSchedulerRow } from "services/installScheduling";

/**
 * Earliest install scheduling offset from self_scheduler_row.accelerated_days_out.
 */
const useInstallSchedulingDaysOut = (
  prospectId: string | undefined,
  serviceAppointmentId: string | undefined
) => {
  const authKey = useAuthToken("HYBRID");
  const { data, error, isLoading, mutate } = useSWR(
    prospectId && serviceAppointmentId && authKey?.token
      ? [
          "Get Self Scheduler Row",
          prospectId,
          serviceAppointmentId,
          authKey.token,
        ]
      : null,
    () =>
      getInstallSelfSchedulerRow(
        authKey!.token,
        prospectId!,
        serviceAppointmentId!
      ) as Promise<SelfSchedulerRowResponse>,
    {
      dedupingInterval: 1000 * 60 * 10,
      errorRetryCount: 0,
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
    }
  );

  const row = resolveSelfSchedulerRow(data);
  const rawDaysOut = row?.accelerated_days_out;
  const daysOut =
    isLoading || !serviceAppointmentId
      ? undefined
      : typeof rawDaysOut === "number" && Number.isFinite(rawDaysOut)
      ? rawDaysOut
      : DEFAULT_INSTALL_SCHEDULING_DAYS_OUT;
  const consecutiveScheduling =
    typeof row?.num_days_for_install === "number" &&
    row.num_days_for_install > 1;
  const numDaysForInstall = row?.num_days_for_install;

  return {
    daysOut,
    customerTimezone: row?.customer_timezone,
    consecutiveScheduling,
    numDaysForInstall,
    isLoading,
    error,
    retry: mutate,
  } as const;
};

export { useInstallSchedulingDaysOut };
